import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import ScrollableAnchor, {
  configureAnchors,
  goToAnchor
} from 'react-scrollable-anchor';
import TabView from '../../../components/TabView';
import CustomerRiskIndicator from '../../../components/tm/CustomerRiskIndicator/index';
import {
  getAlertDetail,
  getClusterViewDescription,
  getCustomerProfile,
  getFeatureCategoryTypes,
  getFeatureCodes,
  getHistAlert,
  getHistAlertHeader,
  getLinkedCustomersC2C,
  getRiskIndicatorsGraph,
  getTransaction,
  getTransactionProductMapping,
  getTransactionTypes,
  resetInvestigationData
} from '../../../redux/actions/tm/investigationDetail';
import AssocTransaction from './AssocTransaction';
import { ClusterInformation } from './ClusterInformation/index';
import CustomerProfile from './CustomerProfile';
import Explainability from './Explainability';
import HistAlert from './HistAlert';
import style from './index.scss';
import InvestDetailHeader from './InvestDetailHeader';
//throwable when backend is ready
import transactionHeader from './transactionHeader';
import CustomerActivity from './CustomerActivity';
import AdditionalDetails from './AdditionalDetails';
import { getAlertIDFromURL } from '../../../services/util';
import moment from 'moment';

import { getItemFromLocalStorage } from '../../../util/localStorageUtils';
import { isUiResourceAvailable } from '../../../util/index';
import { convertDateInputToEpoch } from '../../../util/timezone';
import { getSelectedResources } from '../../../util/resourceManager';
import { orderBy } from '../../../util/lodashlike';

/**
 * tabList for InvestigationDetail page
 * @type {string[]}
 */
const tabList = [
  'Customer Details',
  'Transaction Details',
  'Customer Activity',
  'Alert Details',
  'Alert Score Explainability',
  'Customer Risk View'
];

const { tmUserManagementResources } = getSelectedResources();

/**
 * InvestigationDetail page
 */
class InvestigationDetail extends React.Component {
  // eslint-disable-line react/prefer-stateless-function

  /**
   * componentDidMount
   */
  componentDidMount() {
    const isUsingUserManagement =
      getItemFromLocalStorage('user-management-enabled') === 'true';
    const { authentication, dispatch } = this.props;

    let isCustomerProfileAllowed = isUsingUserManagement ? false : true;
    let isAssociatedCustomersAllowed = isUsingUserManagement ? false : true;
    let isAssociatedAccountsAllowed = isUsingUserManagement ? false : true;
    let isTransactionDetailsAllowed = isUsingUserManagement ? false : true;
    let isCustomerActivityAllowed = isUsingUserManagement ? false : true;
    let isAdditionalInformationAllowed = isUsingUserManagement ? false : true;
    let isAlertsHistoryAllowed = isUsingUserManagement ? false : true;
    let isExplainabilityAllowed = isUsingUserManagement ? false : true;
    let isRiskIndicatorAllowed = isUsingUserManagement ? false : true;
    let isClusterViewAllowed = isUsingUserManagement ? false : true;

    goToAnchor('section0');
    window.addEventListener('click', this.handleClick);
    configureAnchors({ offset: -205, scrollDuration: 300 });
    const { alertID } = getAlertIDFromURL();
    if (dispatch) {
      // start iterating widgets if authentication data is available
      // and if user management is allowed
      if (isUsingUserManagement) {
        // check if authentication data is there (UI resources)
        if (
          authentication &&
          Array.isArray(authentication.data) &&
          authentication.data.length > 0
        ) {
          const listOfUiResources = authentication.data?.map(
            (attributeItem) => {
              return attributeItem?.details?.resource?.resourceCode;
            }
          );

          if (
            isUiResourceAvailable(
              tmUserManagementResources.TM_CUSTOMER_PROFILE_WIDGET,
              listOfUiResources
            )
          ) {
            isCustomerProfileAllowed = true;
          }

          if (
            isUiResourceAvailable(
              tmUserManagementResources.TM_ASSOCIATED_CUSTOMER_WIDGET,
              listOfUiResources
            )
          ) {
            isAssociatedCustomersAllowed = true;
          }

          if (
            isUiResourceAvailable(
              tmUserManagementResources.TM_ASSOCIATED_ACCOUNTS_WIDGET,
              listOfUiResources
            )
          ) {
            isAssociatedAccountsAllowed = true;
          }

          if (
            isUiResourceAvailable(
              tmUserManagementResources.TM_TRANSACTION_DETAILS_WIDGET,
              listOfUiResources
            )
          ) {
            isTransactionDetailsAllowed = true;
          }

          if (
            isUiResourceAvailable(
              tmUserManagementResources.TM_CUSTOMER_ACTIVITY_WIDGET,
              listOfUiResources
            )
          ) {
            isCustomerActivityAllowed = true;
          }

          if (
            isUiResourceAvailable(
              tmUserManagementResources.TM_ADDITIONAL_INFO_WIDGET,
              listOfUiResources
            )
          ) {
            isAdditionalInformationAllowed = true;
          }

          if (
            isUiResourceAvailable(
              tmUserManagementResources.TM_ALERT_DETAILS_WIDGET,
              listOfUiResources
            )
          ) {
            isAlertsHistoryAllowed = true;
          }

          if (
            isUiResourceAvailable(
              tmUserManagementResources.TM_EXPLAINABILITY_WIDGET,
              listOfUiResources
            )
          ) {
            isExplainabilityAllowed = true;
          }

          if (
            isUiResourceAvailable(
              tmUserManagementResources.TM_RISK_INDICATOR_WIDGET,
              listOfUiResources
            )
          ) {
            isRiskIndicatorAllowed = true;
          }

          if (
            isUiResourceAvailable(
              tmUserManagementResources.TM_CLUSTER_INFORMATION_WIDGET,
              listOfUiResources
            )
          ) {
            isClusterViewAllowed = true;
          }

          const alertDetailPayload = {
            id: alertID,
            isCustomerProfileAllowed,
            isAssociatedCustomersAllowed,
            isAssociatedAccountsAllowed,
            isTransactionDetailsAllowed,
            isCustomerActivityAllowed,
            isAdditionalInformationAllowed,
            isAlertsHistoryAllowed,
            isExplainabilityAllowed,
            isRiskIndicatorAllowed,
            isClusterViewAllowed
          };

          this.props.dispatch(getAlertDetail(alertDetailPayload));
          this.props.dispatch(getFeatureCodes({}));
          this.props.dispatch(getTransactionTypes({}));
          this.props.dispatch(getFeatureCategoryTypes({}));
          this.props.dispatch(getTransactionProductMapping({}));
          if (isClusterViewAllowed) {
            this.props.dispatch(getClusterViewDescription({}));
          }
        }
      } else {
        const alertDetailPayload = {
          id: alertID,
          isCustomerProfileAllowed,
          isAssociatedCustomersAllowed,
          isAssociatedAccountsAllowed,
          isTransactionDetailsAllowed,
          isCustomerActivityAllowed,
          isAdditionalInformationAllowed,
          isAlertsHistoryAllowed,
          isExplainabilityAllowed,
          isRiskIndicatorAllowed,
          isClusterViewAllowed
        };

        this.props.dispatch(getAlertDetail(alertDetailPayload));
        this.props.dispatch(getFeatureCodes({}));
        this.props.dispatch(getTransactionTypes({}));
        this.props.dispatch(getFeatureCategoryTypes({}));
        this.props.dispatch(getTransactionProductMapping({}));
        this.props.dispatch(getClusterViewDescription({}));
      }
    }
    // this.props.dispatch(getRiskIndicators())
  }

  /**
   * componentWillUnmount
   */
  componentWillUnmount() {
    window.removeEventListener('click', this.handleClick);

    this.props.dispatch(resetInvestigationData());
  }

  /**
   * onC2CLinkedAccountScrollToLoad
   * @param offset
   */
  onC2CLinkedAccountScrollToLoad = (offset) => {
    const { alertID } = getAlertIDFromURL();
    const { investigationDetail, dispatch } = this.props;

    if (investigationDetail?.alertDetail && dispatch) {
      const { customer_id } = investigationDetail.alertDetail;
      dispatch(
        getLinkedCustomersC2C({
          customerId: customer_id,
          alertId: alertID,
          offset: offset,
          limit: 20,
          isScrolled: true
        })
      );
    }
  };
  /**
   * onCustomerChanged
   * @param customer
   */
  onCustomerChanged = (customer) => {
    const { dispatch } = this.props;

    dispatch(
      getCustomerProfile({
        id: customer.id
      })
    );
  };
  /**
   * onHistAlertDtChanged
   * @param start
   * @param end
   */
  onHistAlertDtChanged = (start, end) => {
    const { alertID } = getAlertIDFromURL();
    const { investigationDetail, dispatch } = this.props;

    if (investigationDetail?.alertDetail && dispatch) {
      const { customer_id } = investigationDetail.alertDetail;
      dispatch(getHistAlertHeader());
      dispatch(
        getHistAlert({
          customerId: customer_id,
          alertId: alertID,
          from: convertDateInputToEpoch(start, true),
          to: convertDateInputToEpoch(end, true),
          offset: 0,
          limit: 10
        })
      );
    }
  };

  /**
   * onHistAlertFilterApplied
   * @param filterPayload
   */
  onHistAlertFilterApplied = (filterPayload) => {
    const { investigationDetail, dispatch } = this.props;

    if (investigationDetail?.alertDetail && dispatch) {
      const { customer_id } = investigationDetail.alertDetail;
      const { alertID } = getAlertIDFromURL();
      this.props.dispatch(getHistAlertHeader());
      this.props.dispatch(
        getHistAlert({
          alertId: alertID,
          offset: 0,
          limit: 10,
          filterPayload,
          customer_id
        })
      );
    }
  };

  /**
   * onHistAlertScrollToLoad
   * @param start
   * @param end
   * @param offset
   */
  onHistAlertScrollToLoad = (start, end, offset) => {
    const { alertID } = getAlertIDFromURL();
    const { investigationDetail, dispatch } = this.props;

    if (investigationDetail?.alertDetail && dispatch) {
      const { customer_id } = investigationDetail.alertDetail;
      dispatch(getHistAlertHeader());
      dispatch(
        getHistAlert({
          customerId: customer_id,
          alertId: alertID,
          from: convertDateInputToEpoch(start, true),
          to: convertDateInputToEpoch(end, true),
          offset: offset,
          limit: 10,
          isScrolled: true
        })
      );
    }
  };
  /**
   * onTransactionHistoryDateChange
   * @param start
   * @param end
   */
  onTransactionHistoryDateChange = (start, end) => {
    const { alertID } = getAlertIDFromURL();
    const { investigationDetail, dispatch } = this.props;

    if (investigationDetail?.alertDetail && dispatch) {
      const { customer_id } = investigationDetail.alertDetail;
      dispatch(
        getTransaction({
          customerId: customer_id,
          alertId: alertID,
          from: convertDateInputToEpoch(start, true),
          to: convertDateInputToEpoch(end, true),
          offset: 0,
          limit: 20
        })
      );
    }
  };

  /**
   * onTransactionHistoryScrollToLoad
   * @param start
   * @param end
   * @param offset
   */
  onTransactionHistoryScrollToLoad = (start, end, offset) => {
    const { alertID } = getAlertIDFromURL();
    const { investigationDetail, dispatch } = this.props;
    if (investigationDetail?.alertDetail && dispatch) {
      const { customer_id } = investigationDetail.alertDetail;
      dispatch(
        getTransaction({
          customerId: customer_id,
          alertId: alertID,
          from: convertDateInputToEpoch(start, true),
          to: convertDateInputToEpoch(end, true),
          offset: offset,
          limit: 20,
          isScrolled: true
        })
      );
    }
  };

  /**
   * onTransactionsApplyFilter
   * @param filters
   */
  onTransactionsApplyFilter = (filters) => {
    const { investigationDetail, dispatch } = this.props;

    if (investigationDetail?.alertDetail && dispatch) {
      const { alertID } = getAlertIDFromURL();
      const { customer_id } = investigationDetail.alertDetail;
      const {
        cd,
        destinations,
        types,
        dateRange: { startDate, endDate }
      } = filters;

      const payload = {
        to: convertDateInputToEpoch(endDate, true),
        from: convertDateInputToEpoch(startDate, true),
        alertId: alertID,
        customerId: customer_id
      };

      // Check if there are any specified destinations
      if (Array.isArray(destinations) && destinations.length > 0) {
        let destinationPayload = '';
        destinations.forEach((o) => {
          destinationPayload += `${o},`;
        });

        payload['destinationCountryCode'] = destinationPayload;
      }

      // Check if there are any specified transaction type
      if (Array.isArray(types) && types.length > 0) {
        let typesPayload = '';
        types.forEach((o) => {
          typesPayload += `${o},`;
        });
        payload['transactionType'] = typesPayload;
      }

      // check if both or no Credit/Debit filter has been selected
      if (!(cd.CR && cd.DR) || (!cd.CR && !cd.DR)) {
        const isCredit = cd.CR;
        if (!cd.CR && cd.DR) {
          payload['isCredit'] = isCredit;
        }
      }

      const requestPayload = {
        ...payload,
        filter: payload
      };

      dispatch(getTransaction(requestPayload));
    }
  };

  /**
   * closeAlert
   * @param alertDetail
   */
  closeAlert = (alertDetail) => {
    const { dispatch } = this.props;

    if (dispatch && alertDetail) {
      const alertId = alertDetail.alertId;
      const payload = {
        alertId,
        isIssue: {
          isIssue: alertDetail.isIssue
        }
      };
      dispatch(closeAlert(payload));
    }
  };

  /**
   * fetchRiskIndicator
   * @param riskIndicator
   */
  fetchRiskIndicator = (riskIndicator) => {
    const { dispatch, investigationDetail } = this.props;

    if (investigationDetail?.alertDetail && dispatch) {
      const { alert_id, customer_id, clusterId, modelId } =
        investigationDetail.alertDetail;

      const payload = {
        clusterId,
        alert_id,
        customer_id,
        riskIndicator,
        modelId
      };

      dispatch(getRiskIndicatorsGraph(payload));
    }
  };

  /**
   * handleClick
   * @param event
   */
  //handle dropdown dismiss
  handleClick(event) {
    //close all the dropdown filters
    if (!event.target.closest('.dropdown')) {
      if (
        !event.target.matches('.dropdownBtn') &&
        (!event.target.parentElement ||
          (event.target.parentElement &&
            !event.target.parentElement.matches('.dropdownBtn')))
      ) {
        if (
          (event.target.parentElement &&
            !event.target.parentElement.parentElement) ||
          (event.target.parentElement?.parentElement &&
            !event.target.parentElement.parentElement.matches('.dropdownBtn'))
        ) {
          const dropdowns = document.getElementsByClassName('dropdownList');

          for (let i = 0; i < dropdowns.length; i++) {
            const openDropdown = dropdowns[i];
            openDropdown.classList.forEach((val, ind) => {
              if (val.indexOf('_show_') >= 0) {
                openDropdown.classList.remove(val);
              }
            });
          }
        }
      }
    }
    //close all the filter windows
    if (
      !event.target.closest('.filterDropdown') &&
      !event.target.closest('.filterBlock')
    ) {
      const filters = document.getElementsByClassName('filterDropdown');

      for (let i = 0; i < filters.length; i++) {
        const openFilters = filters[i];
        openFilters.classList.forEach((val, ind) => {
          if (val.indexOf('_show_') >= 0) {
            openFilters.classList.remove(val);
          }
        });
      }
    }

    //close the investigation note modal
    const modal = Array.prototype.slice.call(
      document.getElementsByClassName('modal')
    );

    modal.forEach(function (item) {
      if (event.target.classList.contains('modal')) {
        item.style.display = 'none';
        document.body.style.overflow = 'visible';
      }
    });
  }

  /**
   * render
   * @returns {*}
   */
  render() {
    const {
      alertDetail,
      clusterInformation,
      // countriesResp,
      featureCodesResp,
      modelExplainability,
      explainability,
      transaction,
      transactionTypesResp,
      featureCategoryResp,
      histAlertHeader,
      histAlertResp,
      riskIndicatorsResp,
      customerProfileResp,
      linkedCustomerC2CTypesResp,
      clusterViewDescription
    } = this.props.investigationDetail;
    const { authentication } = this.props;
    const clusterInfo = clusterInformation?.data || {};
    const { fetched: isFetched = false } = clusterInformation || {};
    const { fetched: isClusterInfoFetched = false } = clusterInfo || {};
    const customers = alertDetail?.data?.customers || [];
    const customerProfile = customerProfileResp || {};
    const additionalInfo = customerProfile?.additional_info_list || {};
    const linkedCustomersResp = linkedCustomerC2CTypesResp || {};
    let riskIndicators =
      riskIndicatorsResp && Array.isArray(riskIndicatorsResp.data)
        ? riskIndicatorsResp.data
        : [];
    const modelExplainabilityTreeData = modelExplainability?.data?.treeView
      ? modelExplainability.data.treeView
      : [];
    const tabularExplainabilityData = modelExplainability?.data?.tabularView
      ? modelExplainability.data.tabularView
      : [];
    const featureViewData = modelExplainability?.data?.featureViewData
      ? modelExplainability.data.featureViewData
      : [];
    const featureCodesData =
      featureCodesResp && Array.isArray(featureCodesResp.data)
        ? featureCodesResp.data
        : [];
    const featureCategoryCodesData =
      featureCategoryResp && Array.isArray(featureCategoryResp.data)
        ? featureCategoryResp.data
        : [];
    const transactionTypeFilterData =
      transactionTypesResp && Array.isArray(transactionTypesResp.data)
        ? transactionTypesResp.data
        : [];
    const transactionHistoryFilters = transactionHeader;

    const isUsingUserManagement =
      getItemFromLocalStorage('user-management-enabled') === 'true';
    // make it default to true if user management is not used
    let isCustomerProfileAllowed = isUsingUserManagement ? false : true;
    let isAssociatedCustomersAllowed = isUsingUserManagement ? false : true;
    let isAssociatedAccountsAllowed = isUsingUserManagement ? false : true;
    let isTransactionDetailsAllowed = isUsingUserManagement ? false : true;
    let isCustomerActivityAllowed = isUsingUserManagement ? false : true;
    let isAdditionalInformationAllowed = isUsingUserManagement ? false : true;
    let isAlertsHistoryAllowed = isUsingUserManagement ? false : true;
    let isExplainabilityAllowed = isUsingUserManagement ? false : true;
    let isRiskIndicatorAllowed = isUsingUserManagement ? false : true;
    let isClusterViewAllowed = isUsingUserManagement ? false : true;

    // start iterating widgets if authentication data is available
    // and if user management is allowed
    if (isUsingUserManagement) {
      // check if authentication data is there (UI resources)
      if (
        authentication &&
        Array.isArray(authentication.data) &&
        authentication.data.length > 0
      ) {
        const listOfUiResources = authentication.data?.map((attributeItem) => {
          return attributeItem?.details?.resource?.resourceCode;
        });

        if (
          isUiResourceAvailable(
            tmUserManagementResources.TM_CUSTOMER_PROFILE_WIDGET,
            listOfUiResources
          )
        ) {
          isCustomerProfileAllowed = true;
        }

        if (
          isUiResourceAvailable(
            tmUserManagementResources.TM_ASSOCIATED_CUSTOMER_WIDGET,
            listOfUiResources
          )
        ) {
          isAssociatedCustomersAllowed = true;
        }

        if (
          isUiResourceAvailable(
            tmUserManagementResources.TM_ASSOCIATED_ACCOUNTS_WIDGET,
            listOfUiResources
          )
        ) {
          isAssociatedAccountsAllowed = true;
        }

        if (
          isUiResourceAvailable(
            tmUserManagementResources.TM_TRANSACTION_DETAILS_WIDGET,
            listOfUiResources
          )
        ) {
          isTransactionDetailsAllowed = true;
        }

        if (
          isUiResourceAvailable(
            tmUserManagementResources.TM_CUSTOMER_ACTIVITY_WIDGET,
            listOfUiResources
          )
        ) {
          isCustomerActivityAllowed = true;
        }

        if (
          isUiResourceAvailable(
            tmUserManagementResources.TM_ADDITIONAL_INFO_WIDGET,
            listOfUiResources
          )
        ) {
          isAdditionalInformationAllowed = true;
        }

        if (
          isUiResourceAvailable(
            tmUserManagementResources.TM_ALERT_DETAILS_WIDGET,
            listOfUiResources
          )
        ) {
          isAlertsHistoryAllowed = true;
        }

        if (
          isUiResourceAvailable(
            tmUserManagementResources.TM_EXPLAINABILITY_WIDGET,
            listOfUiResources
          )
        ) {
          isExplainabilityAllowed = true;
        }

        if (
          isUiResourceAvailable(
            tmUserManagementResources.TM_RISK_INDICATOR_WIDGET,
            listOfUiResources
          )
        ) {
          isRiskIndicatorAllowed = true;
        }

        if (
          isUiResourceAvailable(
            tmUserManagementResources.TM_CLUSTER_INFORMATION_WIDGET,
            listOfUiResources
          )
        ) {
          isClusterViewAllowed = true;
        }
      }
    }

    let formattedRiskIndicators = [];
    const riskIndicatorGraphData =
      customerProfile &&
      Array.isArray(customerProfile.risk_indicators) &&
      customerProfile.risk_indicators.length > 0
        ? customerProfile.risk_indicators
        : [];

    if (
      Array.isArray(riskIndicators) &&
      riskIndicators.length > 0 &&
      Array.isArray(riskIndicatorGraphData) &&
      riskIndicatorGraphData.length > 0
    ) {
      riskIndicatorGraphData.forEach((o, index) => {
        riskIndicators.map((riskIndicatorMetaData) => {
          if (o.riskIndicatorId === riskIndicatorMetaData.riskIndicatorId) {
            formattedRiskIndicators.push({
              ...o,
              ...riskIndicatorMetaData
            });
          }
        });
      });
    }

    formattedRiskIndicators = orderBy(
      formattedRiskIndicators,
      ['importance'],
      ['desc']
    );
    transactionHistoryFilters.data['types'] = transactionTypeFilterData;

    let navigationTabList = null;

    // create tabs list for user management integration
    let userManagementTabList = [];

    // this is to iterate and determine which section index
    // will each section be assigned to
    let tabNavigationIndex = 0;
    let customerDetailsTabNavigationIndex = 0;
    let transactionDetailsTabNavigationIndex = 0;
    let customerActivityTabNavigationIndex = 0;
    let alertDetailsTabNavigationIndex = 0;
    let explainabilityTabNavigationIndex = 0;
    let clusterViewTabNavigationIndex = 0;

    if (
      isCustomerProfileAllowed ||
      isAssociatedCustomersAllowed ||
      isAssociatedAccountsAllowed
    ) {
      userManagementTabList.push('Customer Details');
      customerDetailsTabNavigationIndex = tabNavigationIndex;
      tabNavigationIndex += 1;
    }

    if (isTransactionDetailsAllowed) {
      userManagementTabList.push('Transaction Details');
      transactionDetailsTabNavigationIndex = tabNavigationIndex;
      tabNavigationIndex += 1;
    }

    if (isCustomerActivityAllowed || isAdditionalInformationAllowed) {
      userManagementTabList.push('Customer Activity');
      customerActivityTabNavigationIndex = tabNavigationIndex;
      tabNavigationIndex += 1;
    }

    if (isAlertsHistoryAllowed) {
      userManagementTabList.push('Alert Details');
      alertDetailsTabNavigationIndex = tabNavigationIndex;
      tabNavigationIndex += 1;
    }

    if (isExplainabilityAllowed) {
      userManagementTabList.push('Alert Score Explainability');
      explainabilityTabNavigationIndex = tabNavigationIndex;
      tabNavigationIndex += 1;
    }

    if (isRiskIndicatorAllowed || isClusterViewAllowed) {
      userManagementTabList.push('Customer Risk View');
      clusterViewTabNavigationIndex = tabNavigationIndex;
      // don't increment tab index anymore since this is the last one
    }

    return (
      <div className={style.mainContainer}>
        <InvestDetailHeader
          alertDetail={alertDetail}
          onCloseAlert={this.closeAlert}
        />

        <div className={style.container}>
          <TabView
            defaultIndex={0}
            tabList={
              navigationTabList
                ? navigationTabList
                : isUsingUserManagement
                ? userManagementTabList
                : tabList
            }
          />
        </div>

        <div className={style.investigationDetailsBody}>
          {(isCustomerProfileAllowed ||
            isAssociatedCustomersAllowed ||
            isAssociatedAccountsAllowed) && (
            <ScrollableAnchor
              id={`section${customerDetailsTabNavigationIndex}`}>
              <div>
                <CustomerProfile
                  isCustomerProfileAllowed={isCustomerProfileAllowed}
                  isAssociatedCustomersAllowed={isAssociatedCustomersAllowed}
                  isAssociatedAccountsAllowed={isAssociatedAccountsAllowed}
                  onScrollToLoad={this.onC2CLinkedAccountScrollToLoad}
                  linkedCustomersResp={linkedCustomersResp}
                  customerProfile={customerProfile}
                  clusterData={clusterInfo}
                  customers={customers}
                  additionalInfo={additionalInfo}
                  onCustomerChanged={this.onCustomerChanged}
                />
              </div>
            </ScrollableAnchor>
          )}

          {isTransactionDetailsAllowed && (
            <ScrollableAnchor
              id={`section${transactionDetailsTabNavigationIndex}`}>
              <div className={style.marTop30}>
                <AssocTransaction
                  transactionTypes={transactionTypeFilterData}
                  isLoading={transaction?.isFetching}
                  assocTransactionHeader={transactionHeader}
                  applyFilter={this.onTransactionsApplyFilter}
                  assocTransaction={transaction}
                  onScrollToLoad={this.onTransactionHistoryScrollToLoad}
                  onDateRangeChanged={this.onTransactionHistoryDateChange}
                />
              </div>
            </ScrollableAnchor>
          )}

          {(isCustomerActivityAllowed || isAdditionalInformationAllowed) && (
            <ScrollableAnchor
              id={`section${customerActivityTabNavigationIndex}`}>
              <div className={style.marTop30}>
                {isCustomerActivityAllowed && (
                  <CustomerActivity
                    alertDetail={alertDetail}
                    transactionTypes={transactionTypeFilterData}
                    data={customerProfile}
                  />
                )}
                {isAdditionalInformationAllowed && (
                  <AdditionalDetails
                    alertDetail={alertDetail}
                    transactionTypes={transactionTypeFilterData}
                    data={customerProfile}
                  />
                )}
              </div>
            </ScrollableAnchor>
          )}

          {isAlertsHistoryAllowed && (
            <ScrollableAnchor id={`section${alertDetailsTabNavigationIndex}`}>
              <div className={style.marTop30}>
                <HistAlert
                  isLoading={histAlertResp?.isFetching}
                  histAlertHeader={histAlertHeader}
                  histAlert={histAlertResp || {}}
                  onFilterApplied={this.onHistAlertFilterApplied}
                  onDateRangeChanged={this.onHistAlertDtChanged}
                  onScrollToLoad={this.onHistAlertScrollToLoad}
                />
              </div>
            </ScrollableAnchor>
          )}

          {isExplainabilityAllowed && (
            <ScrollableAnchor id={`section${explainabilityTabNavigationIndex}`}>
              <div className={style.marTop30}>
                <Explainability
                  featureCodesData={featureCodesData}
                  transactionTypeFilterData={transactionTypeFilterData}
                  alertDetail={alertDetail}
                  explainability={explainability}
                  treeData={modelExplainabilityTreeData}
                  featureViewData={featureViewData}
                  tabularData={tabularExplainabilityData}
                />
              </div>
            </ScrollableAnchor>
          )}

          {(isRiskIndicatorAllowed || isClusterViewAllowed) && (
            <div className={style.marTop50}>
              <ScrollableAnchor id={`section${clusterViewTabNavigationIndex}`}>
                <div>
                  {isRiskIndicatorAllowed && (
                    <div>
                      <CustomerRiskIndicator
                        fetchRiskIndicator={this.fetchRiskIndicator}
                        clusterData={clusterInfo}
                        isLoading={isFetched}
                        riskIndicators={formattedRiskIndicators}
                        riskRuleMapping={[]}
                      />
                    </div>
                  )}

                  {isClusterViewAllowed && (
                    <div style={{ marginTop: '15px' }}>
                      <ClusterInformation
                        isLoading={!isClusterInfoFetched}
                        clusterViewDescription={clusterViewDescription}
                        clusterInformation={clusterInfo}
                      />
                    </div>
                  )}
                </div>
              </ScrollableAnchor>
            </div>
          )}
        </div>
      </div>
    );
  }
}

/**
 * proptypes
 * @type {{dispatch: shim}}
 */
InvestigationDetail.propTypes = {
  dispatch: PropTypes.func
};

/**
 * mapStateToProps
 * @param state
 * @param ownProps
 * @returns {{investigationDetail: investigationDetail}}
 */
function mapStateToProps(state, ownProps = {}) {
  return {
    investigationDetail: state.tmInvestigationDetail,
    authentication: state.authentication
  };
}

/**
 * mapDispatchToProps
 * @param dispatch
 * @param ownProps
 * @returns {{dispatch: *, navigateTo: navigateTo}}
 */
function mapDispatchToProps(dispatch, ownProps = {}) {
  return {
    dispatch,
    navigateTo: (location) => {
      dispatch(push(location));
    }
  };
}

/**
 * connect func  for tmInvestigationDetail page
 */
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(InvestigationDetail);

export const InvestigationDetailWithoutStore = InvestigationDetail;
